// The review console — where a human decides, having seen the evidence.
//
// WHAT THIS SCREEN IS FOR
// Not to display a verdict. To let a reviewer disagree with one. Every number
// here is shown next to the verbatim quote it came from, because a score a
// hiring manager cannot check is a score they should not trust — and because a
// candidate who asks "why was I rejected?" deserves an answer more specific than
// "the system said so".
//
// Three things are therefore deliberate:
//   - content and confidence are never combined into one headline number
//   - every competency shows its evidence quote, or says it had none
//   - the decision requires a written reason, and a decision that contradicts
//     the AI is recorded as an override

import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Stack,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import interviewService from "../services/interviewService";
import InterviewReview from "../components/InterviewReview";

const VERDICT_STYLE = {
  STRONG_HIRE: { bg: "#f0fdf4", fg: "#15803d", label: "Strong hire" },
  HIRE: { bg: "#eff6ff", fg: "#0369a1", label: "Hire" },
  BORDERLINE: { bg: "#fffbeb", fg: "#b45309", label: "Borderline" },
  NO_HIRE: { bg: "#fef2f2", fg: "#b91c1c", label: "No hire" },
  // An AI suggestion from a failed hard gate, not a decision — the reason is
  // shown under it. "Rejected" read as though the candidate was turned down.
  REJECTED: { bg: "#fff7ed", fg: "#c2410c", label: "Suggest reject" },
};


const ReviewConsole = () => {
  const { application_id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(null); // 'ADVANCE' | 'REJECT'
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await interviewService.review(application_id);
      setData(res.data?.data || null);
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.statusMessage || "Could not load this interview.",
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [application_id]);

  useEffect(() => {
    load();
  }, [load]);

  const runScoring = async () => {
    setScoring(true);
    try {
      const wasScored = data.session.status === "SCORED";
      await interviewService.score(data.session.id, true);
      await load();
      setNotice(wasScored ? "Re-scored." : "Scoring complete.");
    } catch (err) {
      setError(err.response?.data?.statusMessage || "Scoring failed.");
    } finally {
      setScoring(false);
    }
  };

  const submitDecision = async () => {
    setSaving(true);
    try {
      const res = await interviewService.decide(
        application_id,
        decisionOpen,
        reason,
      );
      setNotice(res.data?.statusMessage || "Decision recorded.");
      setDecisionOpen(null);
      setReason("");
      await load();
    } catch (err) {
      setError(err.response?.data?.statusMessage || "Could not record decision.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#0f766e" }} />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", py: 4 }}>
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  // `rubric` stays: the header shows its version beside the passing bar.
  // `items` and the derived breakdown/signals went to InterviewReview.
  const { session, candidate, rubric, passing_score } = data;
  const verdict = VERDICT_STYLE[session.verdict] || {
    bg: "#f5f5f4",
    fg: "#44403c",
    label: session.verdict || "Not scored",
  };
  // breakdown / signals / weightByKey moved to InterviewReview with the JSX
  // that read them. `data` is passed to it whole, so nothing here needs to
  // reach into the payload on its behalf.

  return (
    <Box sx={{ maxWidth: 980, mx: "auto", pb: 10 }}>
      {/* Sticky, because this page is four screens long and a reviewer deep in
          the transcripts has lost both the name and the verdict they are
          weighing. */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          bgcolor: "rgba(250,250,249,.9)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid #f0efee",
          mx: { xs: -2, md: -3 },
          px: { xs: 2, md: 3 },
          py: 1.25,
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate(-1)}
            sx={{
              textTransform: "none",
              fontWeight: 650,
              fontSize: 13.5,
              color: "#78716c",
              minWidth: 0,
            }}
          >
            Back
          </Button>
          <Box sx={{ width: 1, height: 18, bgcolor: "#e0dedb" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#1c1917" }} noWrap>
            {candidate?.full_name || "Candidate"}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={verdict.label}
            sx={{ bgcolor: verdict.bg, color: verdict.fg, fontWeight: 700, fontSize: 11.5 }}
          />
        </Stack>
      </Box>

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
              {candidate?.full_name || "Candidate"}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              {candidate?.email} · {candidate?.phone}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5 }}>
              Rubric v{rubric?.version} · bar is {passing_score}
            </Typography>
          </Box>
          <Box sx={{ textAlign: { sm: "right" } }}>
            <Chip
              label={verdict.label}
              sx={{ bgcolor: verdict.bg, color: verdict.fg, fontWeight: 800, fontSize: 14, px: 1 }}
            />
            {session.verdict_reason && (
              <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1, maxWidth: 320 }}>
                {session.verdict_reason}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Two scores, side by side, never merged. */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
          <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: "#fafaf9" }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600 }}>
              CONTENT
            </Typography>
            <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.2 }}>
              {session.content_score ?? "—"}
              <Typography component="span" sx={{ fontSize: 15, color: "text.secondary" }}>
                {" "}/ 100
              </Typography>
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              What they know and have done
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: "#fafaf9" }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600 }}>
              CONFIDENCE
            </Typography>
            <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.2 }}>
              {session.confidence_score ?? "—"}
              <Typography component="span" sx={{ fontSize: 15, color: "text.secondary" }}>
                {" "}/ 5
              </Typography>
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              How they came across. Not competence.
            </Typography>
          </Paper>
        </Stack>

        {session.status !== "SCORED" ? (
          <Button
            variant="contained"
            onClick={runScoring}
            disabled={scoring}
            sx={{ mt: 2, textTransform: "none", bgcolor: "#0f766e", "&:hover": { bgcolor: "#115e59" } }}
          >
            {scoring ? "Scoring…" : "Score this interview"}
          </Button>
        ) : (
          // For after a detail the gates read has changed — HR records the
          // real notice period once the transcript was misread, say. The
          // marking runs again, so the score itself can move a little.
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={runScoring}
              disabled={scoring}
              sx={{
                textTransform: "none",
                color: "#0f766e",
                borderColor: "#99d5cf",
                flexShrink: 0,
                "&:hover": { borderColor: "#0f766e", bgcolor: "#f0fdfa" },
              }}
            >
              {scoring ? "Re-scoring…" : "Re-score"}
            </Button>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
              Runs the marking again and re-checks the gates against the notice period recorded
              on the candidate. Use it after correcting their details. The score can change slightly.
            </Typography>
          </Stack>
        )}
      </Paper>

      {/* The interview itself — summary, competency scores and their
          evidence, confidence signals, every answer, and the integrity record.
          Shared with the candidate page so an interview cannot be rendered two
          different ways on two screens. */}
      <Box sx={{ mt: 2 }}>
        <InterviewReview data={data} />
      </Box>

      {/* ── Decision ───────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, mt: 2, borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Typography sx={{ fontWeight: 700 }}>Your decision</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
          You can disagree with the verdict. If you do, it is recorded as an
          override with your reason. This marks the round passed or failed — it
          does not move the candidate to another round.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={() => setDecisionOpen("ADVANCE")}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#15803d", "&:hover": { bgcolor: "#15803d" } }}
          >
            Mark as passed
          </Button>
          <Button
            variant="outlined"
            onClick={() => setDecisionOpen("REJECT")}
            sx={{ textTransform: "none", fontWeight: 700, color: "#b91c1c", borderColor: "#fecdd3" }}
          >
            Reject
          </Button>
        </Stack>
      </Paper>

      <Dialog open={Boolean(decisionOpen)} onClose={() => setDecisionOpen(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {decisionOpen === "ADVANCE"
            ? "Mark this candidate as passed"
            : "Reject this candidate"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 2 }}>
            A reason is required. It is the record of why this decision was made,
            and the only thing that explains it to anyone who asks later.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder="e.g. Strong billing experience and specific examples, worth a human round despite the borderline score."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDecisionOpen(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!reason.trim() || saving}
            onClick={submitDecision}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#0f766e", "&:hover": { bgcolor: "#115e59" } }}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewConsole;
